import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { companyInfo } from '../data/tourData';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - NomadTrailz</title>
        <meta name="description" content="Read our privacy policy to understand how NomadTrailz collects and uses your information." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero Section */}
        <section className="policy-hero">
          <div className="hero-background">
            <img 
              src="https://images.unsplash.com/photo-1464822759844-d150baec4b8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" 
              alt="Mountain Landscape" 
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Privacy Policy
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              How we collect, use, and protect your personal information
            </motion.p>
          </div>
        </section>

        {/* Privacy Content */}
        <section className="policy-content">
          <div className="container">
            <motion.div 
              className="content-card"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="policy-section">
                <h2>1. Information We Collect</h2>
                <p>
                  We collect information you provide directly to us, such as when you:
                </p>
                <ul>
                  <li>Book a tour or make a reservation</li>
                  <li>Contact us for support or inquiries</li>
                  <li>Sign up for our newsletter</li>
                  <li>Participate in surveys or promotions</li>
                  <li>Create an account on our website</li>
                </ul>
                <p>
                  This information may include your name, email address, phone number, 
                  travel preferences, and payment information.
                </p>
                <p>
                  We do not store payment card details; all transactions are processed securely through third-party payment gateways.
                </p>
              </div>

              <div className="policy-section">
                <h2>2. How We Use Your Information</h2>
                <p>
                  We use the information we collect to:
                </p>
                <ul>
                  <li>Process and confirm your bookings</li>
                  <li>Provide customer support and respond to inquiries</li>
                  <li>Send you important updates about your tours</li>
                  <li>Improve our services and develop new offerings</li>
                  <li>Send marketing communications (with your consent)</li>
                  <li>Comply with legal obligations</li>
                  <li>Ensure safety and compliance during travel experiences</li>
                </ul>
              </div>

              <div className="policy-section">
                <h2>3. Information Sharing</h2>
                <p>
                  We do not sell, trade, or rent your personal information to third parties. 
                  We may share your information with:
                </p>
                <ul>
                  <li>Service providers who assist in operating our business</li>
                  <li>Tour operators and accommodation providers</li>
                  <li>Legal authorities when required by law</li>
                  <li>Business partners with your explicit consent</li>
                </ul>
                <p>
                  We require all service providers to handle your data with confidentiality and in accordance with applicable laws.
                </p>
              </div>

              <div className="policy-section">
                <h2>4. Data Security</h2>
                <p>
                  We implement appropriate security measures to protect your personal information 
                  against unauthorized access, alteration, disclosure, or destruction. However, 
                  no method of transmission over the internet is 100% secure.
                </p>
              </div>

              <div className="policy-section">
                <h2>5. Cookies and Tracking</h2>
                <p>
                  We use cookies and similar technologies to enhance your browsing experience, 
                  analyze website traffic, and personalize content. You can control cookie 
                  settings through your browser preferences.
                </p>
              </div>

              <div className="policy-section">
                <h2>6. Your Rights</h2>
                <p>
                  You have the right to:
                </p>
                <ul>
                  <li>Access and review your personal information</li>
                  <li>Update or correct inaccurate information</li>
                  <li>Request deletion of your personal data</li>
                  <li>Opt-out of marketing communications</li>
                  <li>Withdraw consent for data processing</li>
                </ul>
              </div>

              <div className="policy-section">
                <h2>7. Data Retention</h2>
                <p>
                  We retain your personal information for as long as necessary to provide our 
                  services and comply with legal obligations. When we no longer need your 
                  information, we will securely delete or anonymize it.
                </p>
              </div>

              <div className="policy-section">
                <h2>8. International Transfers</h2>
                <p>
                  Your information may be transferred to and processed in countries other than 
                  your own. We ensure appropriate safeguards are in place to protect your data 
                  during such transfers.
                </p>
              </div>

              <div className="policy-section">
                <h2>9. Children's Privacy</h2>
                <p>
                  Our services are not intended for children under 13 years of age. We do not 
                  knowingly collect personal information from children under 13. If you believe 
                  we have collected such information, please contact us immediately.
                </p>
              </div>

              <div className="policy-section">
                <h2>10. Changes to This Policy</h2>
                <p>
                  We may update this privacy policy from time to time. We will notify you of 
                  any material changes by posting the new policy on our website and updating 
                  the "Last Updated" date.
                </p>
              </div>

              <div className="policy-footer">
                <p>
                  <strong>Last Updated:</strong> December 2024<br />
                  <strong>Contact:</strong> For privacy-related questions, please contact us at {companyInfo.email}
                </p>
              </div>
            </motion.div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default PrivacyPolicy;
